import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Target, ArrowRight, RotateCcw } from 'lucide-react';

const QUESTIONS = [
  {
    id: 'mood',
    question: 'Wie ist die Stimmung beim Kunden?',
    emoji: '😊',
    options: [
      { value: 'warm', label: 'Warm & offen', desc: 'Positiv gestimmt, Interesse vorhanden', color: 'green' },
      { value: 'neutral', label: 'Neutral', desc: 'Sachlich, noch unentschlossen', color: 'blue' },
      { value: 'skeptisch', label: 'Skeptisch', desc: 'Kritisch, viele Einwände zu erwarten', color: 'orange' },
    ]
  },
  {
    id: 'reason',
    question: 'Was ist der Hauptgrund für dieses Gespräch?',
    emoji: '🎯',
    options: [
      { value: 'neuprojekt', label: 'Neuprojekt', desc: 'Neues Vorhaben, Budget vorhanden', color: 'green' },
      { value: 'erweiterung', label: 'Erweiterung', desc: 'Bestehende Zusammenarbeit ausbauen', color: 'blue' },
      { value: 'preisverhandlung', label: 'Preisverhandlung', desc: 'Kosten im Fokus, Vergleich mit Wettbewerb', color: 'orange' },
      { value: 'erstgespraech', label: 'Erstgespräch', desc: 'Noch kein konkretes Projekt definiert', color: 'purple' },
    ]
  },
  {
    id: 'priority',
    question: 'Was ist dem Kunden am wichtigsten?',
    emoji: '⭐',
    options: [
      { value: 'qualitaet', label: 'Qualität', desc: 'Professionelles Ergebnis steht an erster Stelle', color: 'blue' },
      { value: 'preis', label: 'Preis / Budget', desc: 'Kosten sind das entscheidende Kriterium', color: 'orange' },
      { value: 'schnelligkeit', label: 'Schnelligkeit', desc: 'Termin und Umsetzungszeit sind kritisch', color: 'red' },
      { value: 'betreuung', label: 'Betreuung', desc: 'Langfristige Partnerschaft gewünscht', color: 'green' },
    ]
  },
  {
    id: 'competition',
    question: 'Gibt es Wettbewerber im Rennen?',
    emoji: '🏁',
    options: [
      { value: 'nein', label: 'Nein', desc: 'Wir sind die erste Anlaufstelle', color: 'green' },
      { value: 'ja', label: 'Ja', desc: 'Mindestens ein weiterer Anbieter im Gespräch', color: 'orange' },
      { value: 'unbekannt', label: 'Unbekannt', desc: 'Nicht kommuniziert worden', color: 'blue' },
    ]
  },
  {
    id: 'budget',
    question: 'Wie ist das Budget-Signaling?',
    emoji: '💰',
    options: [
      { value: 'offen', label: 'Offen & flexibel', desc: 'Budget kein Thema, Ergebnis zählt', color: 'green' },
      { value: 'eingeschraenkt', label: 'Eingeschränkt', desc: 'Festes Budget, wenig Spielraum', color: 'orange' },
      { value: 'unklar', label: 'Noch unklar', desc: 'Budget wurde nicht kommuniziert', color: 'blue' },
    ]
  }
];

const COLOR_MAP = {
  green: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', active: 'bg-green-100 border-green-500' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', active: 'bg-blue-100 border-blue-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-800', active: 'bg-orange-100 border-orange-500' },
  red: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', active: 'bg-red-100 border-red-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-800', active: 'bg-purple-100 border-purple-500' },
};

const SalesCompass = ({ onComplete, onSkip, customerName }) => {
  const [answers, setAnswers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  const currentQ = QUESTIONS[currentStep];
  const isLast = currentStep === QUESTIONS.length - 1;
  const totalAnswered = Object.keys(answers).length;

  const handleSelect = (value) => {
    const newAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(newAnswers);
    if (!isLast) {
      setTimeout(() => setCurrentStep(s => s + 1), 250);
    }
  };

  const handleComplete = () => {
    onComplete(answers);
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentStep(0);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-4 shadow-lg">
          <Target className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Sales-Kompass</h2>
        <p className="text-slate-500">
          {customerName ? `Kalibrierung für: ${customerName}` : '5 kurze Fragen für bessere Coaching-Hinweise'}
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {QUESTIONS.map((q, i) => (
          <div key={q.id} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => i <= totalAnswered && setCurrentStep(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                answers[q.id] ? 'bg-blue-600 text-white' :
                i === currentStep ? 'bg-blue-100 text-blue-700 border-2 border-blue-500' :
                'bg-slate-100 text-slate-400'
              }`}
            >
              {answers[q.id] ? '✓' : i + 1}
            </button>
            {i < QUESTIONS.length - 1 && (
              <div className={`flex-1 h-1 rounded ${answers[q.id] ? 'bg-blue-400' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Question Card */}
      <Card className="shadow-xl border-0 bg-white">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">{currentQ.emoji}</div>
            <h3 className="text-xl font-bold text-slate-900">{currentQ.question}</h3>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {currentQ.options.map((opt) => {
              const colors = COLOR_MAP[opt.color];
              const isSelected = answers[currentQ.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  data-testid={`compass-${currentQ.id}-${opt.value}`}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.01] ${
                    isSelected
                      ? `${colors.active} shadow-md`
                      : `${colors.bg} ${colors.border} hover:shadow-sm`
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center shrink-0 border ${colors.border}`}>
                    {isSelected ? <span className="text-lg">✓</span> : <span className={`text-sm font-bold ${colors.text}`}>{opt.label[0]}</span>}
                  </div>
                  <div>
                    <div className={`font-semibold ${colors.text}`}>{opt.label}</div>
                    <div className="text-sm text-slate-500">{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setCurrentStep(s => s - 1)}>
                  ← Zurück
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-400">
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onSkip} className="text-slate-500">
                Überspringen
              </Button>
              {isLast && answers[currentQ.id] && (
                <Button onClick={handleComplete} className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="compass-complete-btn">
                  Angebot erstellen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {!isLast && answers[currentQ.id] && (
                <Button size="sm" onClick={() => setCurrentStep(s => s + 1)}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  Weiter <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesCompass;
